<template>
  <div class="modal-card card">
    <div class="card-header">
      <h4 class="card-header-title">Export User IDs</h4>
      <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
    </div>
    <div class="card-body">
      <!-- Criterion Display Section -->
      <div v-if="!hasResults" class="mb-4">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h4 class="mb-0">
            <i class="fa fa-filter me-2 text-primary"></i>
            Criteria
          </h4>
          <div class="btn-group btn-group-sm" role="group">
            <button
              v-if="isSimpleCriterion"
              type="button"
              class="btn"
              :class="{ 'btn-primary': criterionMode === 'simple', 'btn-outline-primary': criterionMode !== 'simple' }"
              @click="criterionMode = 'simple'"
            >
              <i class="fa fa-list me-1"></i>
              Simple
            </button>
            <button
              v-if="!isSimpleCriterion"
              type="button"
              class="btn"
              :class="{ 'btn-primary': criterionMode === 'advanced', 'btn-outline-primary': criterionMode !== 'advanced' }"
              @click="criterionMode = 'advanced'"
            >
              <i class="fa fa-filter me-1"></i>
              Advanced
            </button>
            <button
              type="button"
              class="btn"
              :class="{ 'btn-primary': criterionMode === 'json', 'btn-outline-primary': criterionMode !== 'json' }"
              @click="criterionMode = 'json'"
            >
              <i class="fa fa-code me-1"></i>
              JSON
            </button>
          </div>
        </div>

        <!-- Simple Mode -->
        <div v-if="criterionMode === 'simple' && isSimpleCriterion">
          <SimplifiedCriterion
            :criteria="simpleCriteria"
            :columns="availableColumns"
            :column-values="columnValues"
            :loading-values="{}"
            :is-view-only="true"
            :hide-action="true"
          />
        </div>

        <!-- Advanced Mode -->
        <div v-else-if="criterionMode === 'advanced' && !isSimpleCriterion">
          <div v-if="advancedOptions.negated" class="alert alert-warning mb-3">
            <i class="fa fa-ban me-2"></i>
            <strong>Negated:</strong> This criterion matches users NOT meeting the conditions below.
          </div>
          <div class="mb-3 p-3 bg-light rounded">
            <label class="form-label fw-bold">
              <i class="fa fa-layer-group me-2"></i>
              Match Operator
            </label>
            <div class="form-control-plaintext fw-bold">
              {{ advancedOptions.operator === 'AND' ? 'Match ALL of the criterion groups below' : 'Match ANY of the criterion groups below' }}
            </div>
          </div>

          <div class="criterion-groups-container">
            <div
              v-for="(group, groupIndex) in criterionGroups"
              :key="groupIndex"
              class="criterion-group-wrapper mb-3"
            >
              <CriterionGroup
                :group-index="groupIndex"
                :criteria="group.criteria"
                :group-operator="group.operator"
                :groups="group.groups || []"
                :columns="availableColumns"
                :column-values="columnValues"
                :loading-values="{}"
                :is-view-only="true"
              />

              <!-- Group operator divider -->
              <div v-if="groupIndex < criterionGroups.length - 1" class="group-operator-divider my-3">
                <div class="operator-badge">
                  <i :class="advancedOptions.operator === 'AND' ? 'fa fa-plus' : 'fa fa-circle'" class="me-2"></i>
                  {{ advancedOptions.operator }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- JSON Mode -->
        <div v-else-if="criterionMode === 'json'">
          <textarea
            :value="jsonEditorValue"
            class="form-control json-editor"
            rows="15"
            spellcheck="false"
            readonly
          ></textarea>
        </div>
      </div>

      <!-- Configuration Form -->
      <form v-if="!hasResults" @submit.prevent="runExport">
        <div>
          <InputField type="multiselect" v-model="selectedColumns" :options="columns" :searchable="true"
            placeholder="All columns (leave empty for full analysis)" :label="'User ID Columns'" :toolTip="'List of columns to store in the output location'"/>
        </div>

        <div>
          <InputField type="text" v-model="outputLocation" :label="'Output Location'" :toolTip="'Optionally specify the S3 location to save the output at. If omitted, the system chooses the location'" />
        </div>

        <div class="alert alert-info">
          <i class="fa fa-info-circle me-2"></i>
          This will export user IDs matching the audience criteria. Large exports may take some time.
        </div>

        <div v-if="error" class="alert alert-danger">
          {{ error }}
        </div>
      </form>

      <!-- Results Display -->
      <div v-else>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h4 class="mb-0">
            <i class="fa fa-check-circle text-success me-2"></i>
            Export Complete
          </h4>
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="resetResults">
            <i class="fa fa-arrow-left me-1"></i>
            Export Again
          </button>
        </div>

        <div class="total-count-card mb-4">
          <div class="card bg-primary text-white">
            <div class="card-body text-center py-4">
              <h6 class="text-uppercase text-white-50 mb-2">
                Total Rows
              </h6>
              <div class="d-flex align-items-center justify-content-center">
                <h1 class="display-4 fw-bold mb-0">{{ formatNumber(exportResult?.data?.body?.length || 0) }}</h1>
              </div>
            </div>
          </div>
        </div>

        <!-- Preview -->
        <div v-if="exportResult && exportResult.data" class="mt-3">
          <label class="form-label text-muted small mb-2">Preview (first 10 rows)</label>
          <div class="table-responsive" style="max-height: 300px; overflow-y: auto;">
            <table class="table table-sm table-bordered table-hover">
              <thead class="table-light sticky-top">
                <tr>
                  <th v-for="(header, index) in exportResult.data.header" :key="index" class="text-nowrap">
                    {{ header }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, rowIndex) in exportResult.data.body.slice(0, 10)" :key="rowIndex">
                  <td v-for="(cell, cellIndex) in row" :key="cellIndex">
                    {{ cell }}
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="exportResult.data.body.length > 10" class="text-muted small mt-2 text-center">
              ... and {{ formatNumber(exportResult.data.body.length - 10) }} more rows
            </div>
          </div>
        </div>

        <div class="mt-3">
          <InputField 
            type="radio" 
            v-model="exportFormat" 
            :name="'exportFormat'"
            :options="formatOptions"
            :label="'Export Format'" 
            :toolTip="'Select the format for downloading the exported data'" 
          />
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2 text-muted">Exporting user IDs...</p>
      </div>
    </div>
    <div class="card-footer">
      <button
        v-if="!hasResults"
        type="button"
        class="btn btn-primary"
        @click="runExport"
        :disabled="loading"
      >
        <span v-if="loading" class="spinner-border spinner-border-sm me-2" />
        <i v-else class="fa fa-file-export me-1"></i>
        Export IDs
      </button>
      <button
        v-if="hasResults"
        type="button"
        class="btn btn-primary"
        @click="downloadResults"
      >
        <i class="fa fa-download me-1"></i>
        Download {{ exportFormat.toUpperCase() }}
      </button>
      <button
        v-if="hasResults"
        type="button"
        class="btn btn-outline-primary ms-2"
        @click="copyToClipboard"
      >
        <i class="fa fa-copy me-1"></i>
        Copy to Clipboard
      </button>
      <button type="button" class="btn btn-secondary ms-2" data-bs-dismiss="modal">
        {{ hasResults ? 'Close' : 'Cancel' }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import InputField from '../InputField.vue'
import SimplifiedCriterion from '../audience/SimplifiedCriterion.vue'
import CriterionGroup from '../audience/CriterionGroup.vue'
import {
  parseSimpleCriteria,
  parseAdvancedCriteria,
  isSimpleCriterion,
  prePopulateColumnValues,
  prePopulateColumnValuesFromGroups,
} from '../../utils/criterionUtils'

export default {
  name: 'ExportUserIds',
  components: { InputField, SimplifiedCriterion, CriterionGroup },
  props: {
    modalData: Object,
  },
  data() {
    return {
      loading: false,
      error: null,
      exportFormat: 'json',
      exportResult: null, // Store full result with headers and body
      selectedColumns: null,
      outputLocation: '',
      hasResults: false,
      // Criterion display state
      criterionMode: 'simple',
      isSimpleCriterion: true, // Track if criterion is simple or advanced
      availableColumns: [],
      columnValues: {},
      simpleCriteria: [{ column: null, values: [] }],
      advancedOptions: {
        operator: 'OR',
        negated: false,
      },
      criterionGroups: [{
        operator: 'AND',
        criteria: [{
          column: null,
          condition: 'IN',
          values: [],
          lowerbound: null,
          upperbound: null,
          singleValue: null,
        }],
        groups: [],
      }],
      jsonEditorValue: '',
    }
  },
  computed: {
    audience() {
      return this.modalData?.audience
    },
    columns() {
      if (this.modalData?.columns && this.modalData.columns.length > 0) {
        return this.modalData.columns.map(column => ({
          label: column,
          value: column,
        }))
      }
      return []
    },
    formatOptions() {
      return [
        {
          index: 0,
          label: 'CSV',
          value: 'csv'
        },
        {
          index: 1,
          label: 'JSON',
          value: 'json'
        }
      ]
    },
  },
  mounted() {
    this.parseCriteriaFromAudience()
    this.loadAvailableColumns()
  },
  methods: {
    ...mapActions({
      runUserIdQuery: 'audience/runUserIdQuery',
      insertAudienceExecution: 'audience/insertAudienceExecution',
      addToast: 'addToast',
    }),
    async runExport() {
      this.loading = true
      this.error = null

      try {
        const criterion = JSON.parse(this.audience.audience_criterion_json || '{}')

        const queryBody = {
          query_type: 'user_ids',
          dataset: this.audience.audsum_dataset,
          criterion: criterion.criterion || criterion,
          output_location: this.outputLocation,
          user_id_columns: this.selectedColumns,
        }

        const result = await this.runUserIdQuery({
          dataEnvironmentId: this.audience.audsum_data_environment,
          queryBody,
        })

        // Store the full result
        this.exportResult = result
        this.hasResults = true

        // insert the execution in surface audience history
        let requestBody = {
          audsum_endpoint: this.$store.state.audsumUrl,
          audsum_execution_id: this.exportResult.execution_id,
          audience_criterion_snap: this.audience.audience_criterion_json,
          output_location: this.outputLocation || "",
          executed_by: this.$store.state.currentUser.email,
          executed_time: this.formatDateTime(),
          count: this.exportResult?.data?.body?.length || 0,
        }

        await this.insertAudienceExecution({
          audienceId: this.audience.id,
          requestBody,
        });
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Failed to export user IDs'
      } finally {
        this.loading = false
      }
    },
    resetResults() {
      this.hasResults = false
      this.exportResult = null
    },
    formatNumber(num) {
      if (num == null) return '-'
      return num.toLocaleString()
    },
    formatDateTime() {
      const date = new Date()
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },
    downloadResults() {
      if (!this.exportResult?.data) return

      let content, mimeType, extension

      if (this.exportFormat === 'csv') {
        const headers = this.exportResult.data.header.join(',')
        const rows = this.exportResult.data.body.map(row => row.join(',')).join('\n')
        content = headers + '\n' + rows
        mimeType = 'text/csv;charset=utf-8;'
        extension = 'csv'
      } else {
        content = JSON.stringify({
          columns: this.exportResult.data.header,
          data: this.exportResult.data.body,
        }, null, 2)
        mimeType = 'application/json;charset=utf-8;'
        extension = 'json'
      }

      const blob = new Blob([content], { type: mimeType })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `${this.exportResult.execution_id}.${extension}`
      link.click()
      URL.revokeObjectURL(link.href)

      this.addToast({ type: 'success', message: `${extension.toUpperCase()} downloaded successfully` })
    },
    async copyToClipboard() {
      if (!this.exportResult?.data) return

      try {
        const headers = this.exportResult.data.header.join('\t')
        const rows = this.exportResult.data.body.map(row => row.join('\t')).join('\n')
        const text = headers + '\n' + rows

        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
        } else {
          // Fallback for older browsers
          const input = document.createElement('textarea')
          input.style.position = 'absolute'
          input.style.left = '-9999px'
          input.value = text
          document.body.appendChild(input)
          input.select()
          document.execCommand('copy')
          document.body.removeChild(input)
        }
        this.addToast({ type: 'success', message: 'Data copied to clipboard' })
      } catch (error) {
        console.log(error)
        this.addToast({ type: 'error', message: 'Failed to copy to clipboard' })
      }
    },
    loadAvailableColumns() {
      // Use columns from modalData if available
      if (this.modalData?.columns && this.modalData.columns.length > 0) {
        this.availableColumns = this.modalData.columns
      } else {
        this.availableColumns = []
      }
    },
    parseCriteriaFromAudience() {
      if (!this.audience) return

      try {
        const json = this.audience.audience_criterion_json || '{}'
        this.jsonEditorValue = JSON.stringify(JSON.parse(json), null, 2)

        const query = JSON.parse(json)
        if (!query || !query.criterion) {
          this.isSimpleCriterion = true
          this.simpleCriteria = [{ column: null, values: [] }]
          return
        }

        const criterion = query.criterion

        // Check for negation
        let actualCriterion = criterion
        if (criterion.operator === 'not' && criterion.operand) {
          this.advancedOptions.negated = true
          actualCriterion = criterion.operand
        } else {
          this.advancedOptions.negated = false
        }

        // Check if it's a simple AND criterion
        if (isSimpleCriterion(actualCriterion)) {
          this.isSimpleCriterion = true
          this.criterionMode = 'simple'
          this.simpleCriteria = parseSimpleCriteria(actualCriterion)
          // Pre-populate columnValues with existing values so they show in the multiselect
          this.columnValues = prePopulateColumnValues(this.simpleCriteria, this.columnValues)
        } else {
          this.isSimpleCriterion = false
          this.criterionMode = 'advanced'
          const parsed = parseAdvancedCriteria(actualCriterion)
          this.advancedOptions.operator = parsed.operator
          this.criterionGroups = parsed.groups
          // Pre-populate columnValues for advanced mode criteria
          this.columnValues = prePopulateColumnValuesFromGroups(this.criterionGroups, this.columnValues)
        }
      } catch (e) {
        console.error('Failed to parse criteria:', e)
        // If parsing fails, default to JSON mode and assume it's not simple
        this.isSimpleCriterion = false
        this.criterionMode = 'json'
      }
    },
  },
}
</script>

<style scoped>
.form-label {
  font-size: 0.875rem;
  font-weight: 500;
}

.json-editor {
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.875rem;
  line-height: 1.5;
  background-color: #1e1e1e;
  color: #d4d4d4;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  resize: vertical;
  tab-size: 2;
}

.json-editor:focus {
  outline: none;
  border-color: var(--bs-primary);
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
}

.group-operator-divider {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

.group-operator-divider::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 2px;
  background: linear-gradient(to right, transparent, #dee2e6 20%, #dee2e6 80%, transparent);
}

.operator-badge {
  background: linear-gradient(135deg, var(--bs-primary, #2C7BE5), #0056b3);
  color: white;
  padding: 0.5rem 1.5rem;
  border-radius: 2rem;
  font-weight: 600;
  font-size: 0.875rem;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 12px rgba(13, 110, 253, 0.3);
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
}

/* .btn-group .btn {
  flex: 1;
} */

.total-count-card {
  max-width: 100%;
  box-sizing: border-box;
}

.total-count-card .card {
  background: linear-gradient(135deg, #2C7BE5 0%, #1a5cbd 100%);
  box-shadow: 0 4px 20px rgba(44, 123, 229, 0.3);
  max-width: 100%;
  box-sizing: border-box;
}
</style>
